import { useEffect, useRef, useState, useCallback } from "react";
import { Upload, Download, Loader2, FileCheck2, AlertTriangle, ShieldCheck, Save, FileWarning, CheckCircle2, Wallet, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import { BloqueoIntegrantesDialog } from "@/components/common/BloqueoIntegrantesDialog";
import { AvisosDatosFiscales } from "@/components/fiscal/AvisosDatosFiscales";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { productoCredito } from "@/lib/creditos";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TooltipButton } from "@/components/common/Tooltips";
import { IntegrantesGrupoPanel } from "@/components/solicitudes/IntegrantesGrupoPanel";
import { MigracionLineaCampos, MIG_LINEA_VACIA, bodyMigracionLinea, errorMigracionLinea } from "@/components/lineas/MigracionLineaCampos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function RequisitosCredito({ solicitud, onContratado, onLineaGenerada, onIntegrantes }) {
  const { can, user } = useAuth();
  const [migLinea, setMigLinea] = useState(MIG_LINEA_VACIA);
  const puedeSubir = can("documentos", "crear");
  const puedeContratar = can("creditos", "crear");
  const [req, setReq] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [datos, setDatos] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upClave, setUpClave] = useState(null);
  const fileRef = useRef(null);
  // feb-2026 · fix diagnóstico grupal — En lugar de N toasts que se pierden,
  // abrir un diálogo persistente con los integrantes bloqueados y sus impedimentos.
  const [bloqueoDialog, setBloqueoDialog] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/solicitudes/${solicitud.id}/requisitos`).then((x) => x.data);
    setReq(r);
    setDatos(r.datos_credito || {});
    if (r.cliente_id)
      api.get(`/documentos/checklist/${r.cliente_id}`, { params: { solicitud_id: solicitud.id } })
        .then((x) => setKyc(x.data)).catch(() => setKyc(null));
  }, [solicitud.id]);

  useEffect(() => { load(); }, [load]);

  const setCampo = (k, v) => setDatos((p) => ({ ...p, [k]: v }));

  const guardarDatos = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/solicitudes/${solicitud.id}/datos-credito`, { datos_credito: datos });
      setReq((p) => ({ ...p, datos_credito: datos, evaluacion: data.evaluacion }));
      toast.success("Datos del crédito guardados");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const pickFile = (clave) => { setUpClave(clave); fileRef.current?.click(); };

  // jul-2026 fix 3: subir un documento canónico del checklist KYC BASE (curp / constancia_fiscal)
  // directamente desde el modal de originación. Se guarda con `categoria=expediente` y la clave
  // canónica → aparece Vigente al vuelo en el checklist tras el `load()`.
  const [upKycClave, setUpKycClave] = useState(null);
  const kycFileRef = useRef(null);
  const pickKyc = (canonical) => { setUpKycClave(canonical); kycFileRef.current?.click(); };
  const onKycFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !upKycClave || !req?.cliente_id) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", upKycClave);
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Soporte");
      fd.append("cliente_id", req.cliente_id);
      await api.post("/documentos/upload", fd);
      toast.success("Documento cargado en el expediente del cliente");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); setUpKycClave(null); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !upClave) return;
    // feb-2026 fix 4: nunca subir un doc de crédito sin titular. Si la solicitud aún no está
    // ligada a un cliente (p.ej. creada desde el simulador), el doc quedaba huérfano en BD y
    // no aparecía en originación. Bloquear con toast claro es preferible al silencio.
    if (!req?.cliente_id) {
      toast.error("La solicitud aún no está ligada a un cliente. Ábrela desde Originación con un cliente asignado antes de subir documentos.");
      setUpClave(null);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", upClave);
      fd.append("categoria", "credito");
      fd.append("clasificacion", "Requisito de crédito");
      fd.append("cliente_id", req.cliente_id);
      fd.append("referencia_id", solicitud.id);
      await api.post("/documentos/upload", fd);
      toast.success("Documento cargado y cifrado");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); setUpClave(null); }
  };

  const descargar = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const contratar = async () => {
    setBusy(true);
    try {
      // feb-2026: si el producto elegido exige obligados solidarios, validar completos
      // ANTES de contratar. El endpoint devuelve 400 con `detail.faltantes` cuando faltan.
      try {
        await api.post(`/solicitudes/${solicitud.id}/validar-obligados-solidarios`, {});
      } catch (ve) {
        const detail = ve?.response?.data?.detail;
        if (detail && typeof detail === "object") {
          toast.error(detail.mensaje || "Faltan datos del obligado solidario. Completa la solicitud antes de contratar.");
        } else {
          toast.error(apiError(ve));
        }
        setBusy(false);
        return;
      }
      const { data } = await api.post(`/solicitudes/${solicitud.id}/contratar`, { datos_credito: datos });
      const catTxt = data?.credito?.cat != null ? ` · CAT ${data.credito.cat}%` : "";
      toast.success(`Crédito ${data.numero_credito} generado correctamente${catTxt}`);
      (data.advertencias || []).forEach((a) => toast.warning(a, { duration: 8000 }));
      // feb-2026: propaga los obligados solidarios de la solicitud al crédito recién creado
      // (denormalización para cobranza). NO altera cálculo. Falla silente: el crédito sigue.
      const credId = data?.credito?.id || data?.credito_id || data?.id;
      if (credId) {
        try {
          await api.post(`/creditos/${credId}/hidratar-obligados-solidarios`, {});
        } catch (he) {
          // No frenamos el flujo; el operador puede reintentar desde cobranza si fuera necesario.
          const dt = he?.response?.data?.detail;
          if (dt && typeof dt === "object") toast.warning(dt.mensaje || "Obligados solidarios pendientes de propagar.");
        }
      }
      onContratado && onContratado(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.integrantes_bloqueados) && detail.integrantes_bloqueados.length > 0) {
        // feb-2026 · reutiliza el diálogo compartido de impedimentos también en individual.
        setBloqueoDialog({
          mensaje: detail.mensaje || "No se puede contratar",
          integrantes: detail.integrantes_bloqueados,
        });
      } else if (detail && typeof detail === "object") {
        toast.error(detail.mensaje || "Faltan requisitos obligatorios");
      } else {
        toast.error(apiError(e));
      }
      await load();
    } finally { setBusy(false); }
  };

  // Los integrantes llegan precargados de la solicitud; aquí solo viajan sus montos ajustados.
  const contratarGrupal = async (integrantes) => {
    setBusy(true);
    try {
      const payload = { datos_credito: datos, integrantes };
      const { data } = await api.post(`/solicitudes/${solicitud.id}/contratar-grupal`, payload);
      const catTxt = data?.credito?.cat != null ? ` · CAT grupo ${data.credito.cat}%` : "";
      toast.success(`Crédito grupal ${data.numero_credito} generado (${data.integrantes.length} obligados solidarios)${catTxt}`);
      (data.advertencias || []).forEach((a) => toast.warning(a, { duration: 8000 }));
      onContratado && onContratado(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.integrantes_bloqueados) && detail.integrantes_bloqueados.length > 0) {
        // Diálogo persistente: nombre + rol + impedimentos por integrante.
        setBloqueoDialog({
          mensaje: detail.mensaje || "No se puede contratar el grupo",
          integrantes: detail.integrantes_bloqueados,
        });
      } else if (detail && typeof detail === "object") {
        toast.error(detail.mensaje || "No se pudo contratar el grupo", { duration: 9000 });
        (detail.faltantes_campos || []).forEach((f) => toast.error(`Falta dato: ${f.label}`));
      } else { toast.error(apiError(e)); }
    } finally { setBusy(false); }
  };

  // Fase 4 (feb-2026) — Línea de crédito revolvente: genera la línea desde la solicitud
  // Autorizada. NO comparte flujo con `/contratar` (que sigue bloqueado con 409 para este tipo).
  const generarLinea = async () => {
    const errMig = errorMigracionLinea(migLinea);
    if (errMig) { toast.error(errMig); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/lineas/desde-solicitud/${solicitud.id}`, bodyMigracionLinea(migLinea));
      toast.success(`Línea ${data.numero_linea} generada por ${mxn(data.limite_autorizado)}`);
      onLineaGenerada && onLineaGenerada(data);
    } catch (e) {
      toast.error(apiError(e));
      await load();
    } finally { setBusy(false); }
  };

  if (!req) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const ev = req.evaluacion || {};
  const yaContratado = !!req.credito_id || req.estatus === "Contratado";
  const okContratar = ev.ok && !yaContratado;
  const esGrupal = req.modalidad === "Grupal";
  // Fase 4 (feb-2026) — Línea de crédito revolvente: bandera derivada de la clave canónica
  // que resuelve el backend. Cambia SOLO el UI final (panel de acción); el resto del
  // componente (datos, docs, checklist KYC) se comparte con crédito simple.
  const esLineaRevolvente = req.clave === "LINEA_REVOLVENTE";
  const yaTieneLinea = !!req.linea_id;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="requisitos-credito">
      <input ref={fileRef} type="file" className="hidden" accept="application/pdf,.pdf" onChange={onFile} />

      <h4 className="mb-1 flex items-center gap-2 font-heading text-sm font-semibold text-slate-800">
        {/* sep-2026 · El producto REAL, con la misma función que el Perfil 360: `tipo_label`
            es la etiqueta FIJA del instrumento del catálogo (requisitos.py), no el producto
            que se contrató. Si la solicitud no trae producto, cae a esa etiqueta. */}
        <FileCheck2 className="h-4 w-4 text-primary" /> Requisitos del crédito ·{" "}
        {productoCredito({ ...solicitud, tipo_credito_label: req.tipo_label })}
      </h4>
      <p className="mb-3 text-[11px] text-slate-400">
        Campos y documentos exigidos por el tipo de producto. El crédito no se puede generar si falta un requisito obligatorio.
      </p>

      {/* Datos dinámicos */}
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Datos del crédito</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {req.campos_datos.map((c) => (
          <div key={c.clave}>
            <Label className="mb-1 block text-xs font-medium text-slate-600">
              {c.label}{c.obligatorio && <span className="ml-1 text-red-500">*</span>}
            </Label>
            {c.tipo === "select" ? (
              <Select value={datos[c.clave] ?? ""} onValueChange={(v) => setCampo(c.clave, v)}>
                <SelectTrigger data-testid={`req-campo-${c.clave}`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {(c.opciones || []).map((o) => {
                    const val = typeof o === "string" ? o : o.value;
                    const lbl = typeof o === "string" ? o : o.label;
                    return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid={`req-campo-${c.clave}`} type={c.tipo === "number" ? "number" : c.tipo === "date" ? "date" : "text"}
                value={datos[c.clave] ?? ""} disabled={yaContratado}
                onChange={(e) => setCampo(c.clave, e.target.value)} />
            )}
          </div>
        ))}
      </div>
      {!yaContratado && (
        <div className="mt-3">
          <Button data-testid="req-guardar-datos" size="sm" variant="outline" onClick={guardarDatos} disabled={saving || !puedeSubir}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar datos
          </Button>
        </div>
      )}

      {/* Crédito grupal — integrantes capturados en la solicitud, revisados por el Comité. */}
      {esGrupal && !esLineaRevolvente && !yaContratado && (
        <IntegrantesGrupoPanel solicitudId={solicitud.id} onGuardado={onIntegrantes}
          onContratar={contratarGrupal} busy={busy} puedeContratar={puedeContratar} />
      )}
      {esGrupal && !esLineaRevolvente && yaContratado && (
        <div className="mt-5 rounded-lg border-2 px-3 py-3" style={{ borderColor: "#10B981" }}>
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> Crédito grupal generado: {solicitud.numero_credito || "—"}
          </p>
        </div>
      )}

      {/* Documentos del crédito (solo individual) */}
      {!esGrupal && (<>
      <p className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-primary">Documentos requeridos</p>
      <div className="space-y-2">
        {(ev.docs_estado || []).map((d) => (
          <div key={d.clave} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            data-testid={`req-doc-${d.clave}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-slate-700">
                {d.presente ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <FileWarning className="h-4 w-4 text-amber-500" />}
                {d.label}{d.obligatorio ? <span className="text-red-500">*</span> : <span className="text-[10px] text-slate-400"> (opcional)</span>}
              </span>
              <div className="flex items-center gap-2">
                {d.reutilizado && (
                  <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700"
                    data-testid={`req-doc-reutilizado-${d.clave}`}>Reutilizado del cliente</span>
                )}
                <StatusBadge value={d.presente ? "Cargado" : "Faltante"} />
                {d.presente && d.doc_id && (
                  <TooltipButton tooltip="Descargar el archivo (enlace temporal firmado)"
                    data-testid={`req-descargar-${d.doc_id}`} size="sm" variant="outline" className="h-7 px-2" onClick={() => descargar(d.doc_id)}>
                    <Download className="h-3.5 w-3.5" />
                  </TooltipButton>
                )}
                {!yaContratado && (
                  <TooltipButton tooltip="Subir el archivo de este requisito (SOLO PDF)"
                    data-testid={`req-doc-upload-${d.clave}`} size="sm" variant="outline" className="h-7 px-2"
                    disabled={busy || !puedeSubir} onClick={() => pickFile(d.clave)}>
                    {busy && upClave === d.clave ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  </TooltipButton>
                )}
              </div>
            </div>
            {/* Mensaje aclaratorio jul-2026 (iter_78): cuando falta un doc, el usuario debe
                saber que el sistema YA revisó el expediente completo del cliente. Si no lo
                encontró es porque el cliente aún no ha subido este documento — hay que
                subirlo por primera vez, no es un error. */}
            {!d.presente && !yaContratado && (
              <p className="mt-1.5 text-[10.5px] text-slate-500 italic"
                data-testid={`req-doc-hint-${d.clave}`}>
                El cliente no tiene este documento en su expediente. Súbelo por primera vez
                — quedará vigente y disponible para futuros créditos.
              </p>
            )}
          </div>
        ))}
      </div>
      </>)}

      {/* Checklist KYC base */}
      {kyc && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Checklist KYC base ({kyc.tipo_persona}) — {kyc.completos}/{kyc.total} vigentes
          </p>
          <input ref={kycFileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onKycFile} data-testid="kyc-file-input" />
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {kyc.items.map((i) => (
              <div key={i.tipo_documento} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-xs" data-testid={`kyc-row-${i.canonical}`}>
                <span className="text-slate-600">{i.tipo_documento}</span>
                <div className="flex items-center gap-1.5">
                  <StatusBadge value={i.estatus} />
                  {!i.presente && !yaContratado && (
                    <TooltipButton tooltip="Subir este documento al expediente del cliente (queda vigente para futuros créditos)"
                      data-testid={`kyc-upload-${i.canonical}`} size="sm" variant="outline" className="h-6 px-1.5"
                      disabled={busy || !puedeSubir} onClick={() => pickKyc(i.canonical)}>
                      {busy && upKycClave === i.canonical ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    </TooltipButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Garantía */}
      {req.genera_garantia && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="req-garantia-aviso">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          Este tipo de crédito genera garantía automática: <b className="mx-1">{req.garantia_tipo}</b> — {req.garantia_descripcion}.
        </div>
      )}

      {/* Advertencias */}
      {(ev.advertencias || []).map((a, idx) => (
        <div key={idx} className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="req-advertencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {a}
        </div>
      ))}

      {req.avisos_fiscales?.length > 0 && (
        <div className="mt-3"><AvisosDatosFiscales avisos={req.avisos_fiscales} testid="req-avisos-fiscales" /></div>
      )}

      {/* Faltantes + Contratar (individual, NO revolvente) */}
      {!esGrupal && !esLineaRevolvente && (
      <div className="mt-5 rounded-lg border-2 px-3 py-3"
        style={{ borderColor: yaContratado ? "#10B981" : okContratar ? "#10B981" : "#F59E0B" }}>
        {yaContratado ? (
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> Crédito generado: {req.credito_id ? (solicitud.numero_credito || "—") : "—"}
          </p>
        ) : ev.ok ? (
          <p className="text-sm font-medium text-emerald-700">✓ Todos los requisitos obligatorios están completos.</p>
        ) : (
          <div className="text-xs text-amber-800">
            <p className="mb-1 font-semibold">Faltan requisitos obligatorios para contratar:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {(ev.faltantes_campos || []).map((f) => <li key={`c-${f.clave}`}>Dato: {f.label}</li>)}
              {(ev.faltantes_docs || []).map((f) => <li key={`d-${f.clave}`}>Documento: {f.label}</li>)}
            </ul>
          </div>
        )}
        {!yaContratado && (
          <div className="mt-3">
            <TooltipButton tooltip="Genera el crédito y su tabla de amortización"
              data-testid="req-contratar-btn" onClick={contratar} disabled={!okContratar || busy || !puedeContratar}
              className="bg-accent hover:bg-forest-700">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileCheck2 className="mr-1.5 h-4 w-4" />}
              Contratar y generar crédito
            </TooltipButton>
          </div>
        )}
      </div>
      )}

      {/* Fase 4 (feb-2026) — Línea de crédito revolvente: acción propia (NO contratar simple).
          Si la solicitud ya generó línea, muestra el enlace al detalle en lugar del botón. */}
      {esLineaRevolvente && (
      <div className="mt-5 rounded-lg border-2 px-3 py-3"
        data-testid="linea-revolvente-panel"
        style={{ borderColor: yaTieneLinea ? "#10B981" : ev.ok ? "#10B981" : "#F59E0B" }}>
        {yaTieneLinea ? (
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Línea de crédito generada para esta solicitud.
            </p>
            <TooltipButton tooltip="Abrir el detalle de la línea"
              data-testid="req-ver-linea-btn" size="sm"
              onClick={() => onLineaGenerada && onLineaGenerada({ id: req.linea_id })}
              className="bg-accent hover:bg-forest-700">
              Ver línea <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </TooltipButton>
          </div>
        ) : ev.ok ? (
          <p className="text-sm font-medium text-emerald-700">✓ Todos los requisitos obligatorios están completos.</p>
        ) : (
          <div className="text-xs text-amber-800">
            <p className="mb-1 font-semibold">Faltan requisitos obligatorios para generar la línea:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {(ev.faltantes_campos || []).map((f) => <li key={`c-${f.clave}`}>Dato: {f.label}</li>)}
              {(ev.faltantes_docs || []).map((f) => <li key={`d-${f.clave}`}>Documento: {f.label}</li>)}
            </ul>
          </div>
        )}
        {!yaTieneLinea && user?.role === "admin_entidad" && (
          <div className="mt-3">
            <MigracionLineaCampos value={migLinea} onChange={setMigLinea} tipo="linea" prefijo="linea" />
          </div>
        )}
        {!yaTieneLinea && (
          <div className="mt-3">
            <TooltipButton tooltip="Genera la línea de crédito revolvente a partir de la solicitud Autorizada"
              data-testid="req-generar-linea-btn" onClick={generarLinea}
              disabled={!ev.ok || busy || !puedeContratar}
              className="bg-accent hover:bg-forest-700">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wallet className="mr-1.5 h-4 w-4" />}
              Generar línea de crédito
            </TooltipButton>
          </div>
        )}
      </div>
      )}

      {/* Diálogo persistente con los integrantes bloqueados (feb-2026). */}
      <BloqueoIntegrantesDialog
        data={bloqueoDialog ? { mensaje: bloqueoDialog.mensaje, integrantes: bloqueoDialog.integrantes } : null}
        onClose={() => setBloqueoDialog(null)}
      />
    </div>
  );
}
